"use client"

import type { LucideIcon } from "lucide-react"
import { TrendingUp, TrendingDown, DollarSign, Activity, Target, Shield } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import type { BackendPortfolio } from "@/lib/agent-force"
import { computeWinRateForDays } from "@/lib/trade-stats"

const WIN_RATE_LOOKBACK_DAYS = 14

function money(v: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v)
}

const DASH = "—"

const LOADING_TITLES: { title: string; icon: LucideIcon }[] = [
  { title: "Total Portfolio Value", icon: DollarSign },
  { title: "24h P&L", icon: TrendingUp },
  { title: "Active Positions", icon: Activity },
  { title: "Win Rate (14d)", icon: Target },
  { title: "Free Margin", icon: Activity },
  { title: "Margin Level", icon: Shield },
]

function fin(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n)
}

function buildMetrics(
  portfolio: BackendPortfolio | null,
  tradeItems: Array<Record<string, unknown>>,
): Array<{
  title: string
  value: string
  change: string
  trend: "up" | "down" | "neutral"
  icon: LucideIcon
  subtext: string
}> {
  const p = portfolio
  const equity = p != null && fin(p.equity) ? p.equity : null
  const balance = p != null && fin(p.balance) ? p.balance : null
  const margin = p != null && fin(p.margin) ? p.margin : null
  const freeMargin = p != null && fin(p.free_margin) ? p.free_margin : null
  const marginLevel = p != null && fin(p.margin_level) ? p.margin_level : null
  const positionsCount = Array.isArray(p?.positions) ? p.positions.length : null

  let changePct: number | null = null
  if (equity != null && balance != null && balance > 0) {
    changePct = ((equity - balance) / balance) * 100
  }
  const pctLabel =
    changePct != null ? `${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%` : DASH
  const trendFromPct: "up" | "down" | "neutral" =
    changePct == null ? "neutral" : changePct >= 0 ? "up" : "down"

  const pnl = equity != null && balance != null ? equity - balance : null
  const pnlValue =
    pnl != null ? `${pnl >= 0 ? "+" : "-"}${money(Math.abs(pnl))}` : DASH

  const marginSub =
    margin != null ? `Margin used ${money(margin)}` : "Margin used not reported"

  const winRate14d = computeWinRateForDays(tradeItems, WIN_RATE_LOOKBACK_DAYS)
  const winRateValue =
    winRate14d.winRate != null ? `${(winRate14d.winRate * 100).toFixed(1)}%` : DASH
  const winRateChange =
    winRate14d.closedCount > 0
      ? `${winRate14d.wins}W / ${winRate14d.losses}L`
      : DASH
  const winRateTrend: "up" | "down" | "neutral" =
    winRate14d.winRate == null
      ? "neutral"
      : winRate14d.winRate >= 0.5
        ? "up"
        : "down"
  const winRateSubtext =
    winRate14d.closedCount > 0
      ? `${winRate14d.closedCount} closed in last ${WIN_RATE_LOOKBACK_DAYS} days`
      : `No closed trades in last ${WIN_RATE_LOOKBACK_DAYS} days`

  return [
    {
      title: "Total Portfolio Value",
      value: equity != null ? money(equity) : DASH,
      change: pctLabel,
      trend: trendFromPct,
      icon: DollarSign,
      subtext: balance != null ? `vs. ${money(balance)} balance` : "Balance not reported",
    },
    {
      title: "24h P&L",
      value: pnlValue,
      change: pctLabel,
      trend: trendFromPct,
      icon: TrendingUp,
      subtext: "Estimated from equity vs balance",
    },
    {
      title: "Active Positions",
      value: positionsCount != null ? String(positionsCount) : DASH,
      change: positionsCount != null ? `${positionsCount} open` : DASH,
      trend: positionsCount != null && positionsCount > 0 ? "up" : "neutral",
      icon: Activity,
      subtext: "Open instruments",
    },
    {
      title: "Win Rate (14d)",
      value: winRateValue,
      change: winRateChange,
      trend: winRateTrend,
      icon: Target,
      subtext: winRateSubtext,
    },
    {
      title: "Free Margin",
      value: freeMargin != null ? money(freeMargin) : DASH,
      change: freeMargin != null || margin != null ? "Live" : DASH,
      trend: "neutral",
      icon: Activity,
      subtext: marginSub,
    },
    {
      title: "Margin Level",
      value: marginLevel != null ? `${marginLevel.toFixed(2)}%` : DASH,
      change: marginLevel != null ? "Live" : DASH,
      trend: "neutral",
      icon: Shield,
      subtext: "From MT5 portfolio snapshot",
    },
  ]
}

export function PortfolioOverview({
  portfolio,
  tradeItems,
  loading,
}: {
  portfolio?: BackendPortfolio | null
  tradeItems?: Array<Record<string, unknown>>
  loading?: boolean
}) {
  const gridClass =
    "grid w-full min-w-0 gap-3 sm:gap-4 grid-cols-1 min-[28rem]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6"

  if (loading) {
    return (
      <div className={gridClass}>
        {LOADING_TITLES.map((m) => (
          <Card key={m.title} className="bg-card border-border min-w-0 w-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider truncate pr-2">
                {m.title}
              </CardTitle>
              <m.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            </CardHeader>
            <CardContent className="flex min-h-[104px] flex-col items-center justify-center gap-2 py-6">
              <Spinner className="size-8 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Loading portfolio…</p>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const metrics = buildMetrics(portfolio ?? null, tradeItems ?? [])

  return (
    <div className={gridClass}>
        {metrics.map((metric) => (
          <Card key={metric.title} className="bg-card border-border min-w-0 w-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider truncate pr-2">
                {metric.title}
              </CardTitle>
              <metric.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            </CardHeader>
            <CardContent className="min-w-0">
              <div className="text-2xl font-bold font-mono tabular-nums break-words">{metric.value}</div>
              <div className="mt-1 flex min-h-[1.25rem] items-center gap-2">
                {metric.trend === "neutral" ? (
                  <span className="text-xs font-medium text-muted-foreground">{metric.change}</span>
                ) : (
                  <span
                    className={`flex items-center text-xs font-medium ${
                      metric.trend === "up" ? "text-positive" : "text-negative"
                    }`}
                  >
                    {metric.trend === "up" ? (
                      <TrendingUp className="mr-0.5 h-3 w-3" />
                    ) : (
                      <TrendingDown className="mr-0.5 h-3 w-3" />
                    )}
                    {metric.change}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground break-words">{metric.subtext}</p>
            </CardContent>
          </Card>
        ))}
    </div>
  )
}
